import {
  DeleteUserPDFRequest,
  fetchMainServiceRequestPDFDownloadList,
  UpdateUserPDFName,
} from 'src/services/client';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { SuccessMessages } from 'src/common/constants';
import download from 'downloadjs';
import { validatePDFFileName } from 'src/pages/Request/CustomHooks/helpers';

export const useServiceRequestPDFs = (userID: string) => {
  const [serviceRequestPDFDownloadList, setServiceRequestPDFDownloadList] =
    useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [editFormData, setEditFormData] = useState({
    pdfName: '',
    pdfId: null,
    isSubmitting: false,
    validationError: '',
  });

  const fetchPDFs = async () => {
    setLoading(true);
    setError(null);
    try {
      const response: any =
        await fetchMainServiceRequestPDFDownloadList(userID);
      if (response.status === 200 || response.ok) {
        const responsejson = await response.json();
        setServiceRequestPDFDownloadList(responsejson.data);
      } else {
        throw new Error('Failed to fetch PDF requests.');
      }
    } catch (err: any) {
      console.error(err);
      setError('An error occurred while fetching PDF requests.');
      toast.error(
        '🚫 Sorry, cannot fetch PDF requests at this time. Please try again later.'
      );
    } finally {
      setLoading(false);
    }
  };

  const initializeEditForm = (pdfData: any) => {
    setEditFormData({
      pdfName: pdfData.fileName,
      pdfId: pdfData.id,
      isSubmitting: false,
      validationError: '',
    });
  };

  const updatePdfName = (newName: string) => {
    setEditFormData((prev) => ({
      ...prev,
      pdfName: newName,
      validationError: '',
    }));
  };

  const validateAndSubmitEdit = async (): Promise<boolean> => {
    const validation = validatePDFFileName(editFormData.pdfName);

    if (!validation.isValid) {
      setEditFormData((prev) => ({
        ...prev,
        validationError: validation.errorMessage,
      }));
      return false;
    }

    setEditFormData((prev) => ({
      ...prev,
      isSubmitting: true,
      validationError: '',
    }));

    try {
      const userDetails = JSON.parse(localStorage.getItem('user') || '{}');

      if (!userDetails.id) {
        toast.error('User not found. Please log in again.', {
          autoClose: 5000,
          position: 'top-center',
        });

        setEditFormData((prev) => ({ ...prev, isSubmitting: false }));
        return false;
      }

      const response: any = await UpdateUserPDFName(
        userDetails.id,
        editFormData.pdfId,
        editFormData.pdfName
      );

      if (!response.ok && response.status !== 200) {
        toast.error('Failed to update PDF name. Please try again.', {
          autoClose: 5000,
          position: 'top-center',
        });

        setEditFormData((prev) => ({ ...prev, isSubmitting: false }));
        return false;
      }

      const responseJson = await response.json();

      if (responseJson.statusCode !== 200) {
        if (responseJson.message) {
          toast.error(responseJson.message, {
            autoClose: 5000,
            position: 'top-center',
          });
        }

        setEditFormData((prev) => ({ ...prev, isSubmitting: false }));
        return false;
      }

      await fetchPDFs();

      if (responseJson.message) {
        toast.success(responseJson.message, {
          autoClose: 10000,
          position: 'top-center',
        });
      }

      const resetFormState = {
        pdfName: '',
        pdfId: null,
        isSubmitting: false,
        validationError: '',
      };

      setEditFormData(resetFormState);

      return true;
    } catch (error) {
      console.error('Error updating PDF name:', error);
      toast.error(
        'An unexpected error occurred while updating the file name. Please try again.',
        {
          autoClose: 5000,
          position: 'top-center',
        }
      );

      setEditFormData((prev) => ({ ...prev, isSubmitting: false }));
      return false;
    }
  };

  const deletePDF = async (unitId: string, userId: string) => {
    try {
      const response: any = await DeleteUserPDFRequest(userId, unitId);

      if (response.ok || response.status === 200) {
        const responsejson = await response.json();
        if (responsejson.statusCode === 200) {
          toast.success('ⓘ PDF has been deleted.', {
            autoClose: 10000,
            position: 'top-center',
          });
          fetchPDFs();
        } else {
          throw new Error('API returned non-200 status code');
        }
      } else {
        throw new Error('Request failed with non-200 status');
      }
    } catch (err) {
      console.error(err);
      toast.error(
        '🚫 Sorry, cannot delete PDF at this time. Please try again later.',
        {
          autoClose: 5000,
          position: 'top-center',
        }
      );
    }
  };

  const exportPDF = (pdfurl: string) => {
    if (pdfurl !== null) {
      toast.info(SuccessMessages.pdfDownloadNotification, {
        position: 'top-center',
      });
      download(pdfurl);
    } else {
      toast.error(
        '🚫 PDF preparation is still in progress, please try again after some time.',
        { autoClose: 12000, position: 'top-center' }
      );
    }
  };

  useEffect(() => {
    if (userID) {
      fetchPDFs();
    }
  }, [userID]);

  return {
    serviceRequestPDFDownloadList,
    loading,
    error,
    fetchPDFs,
    deletePDF,
    exportPDF,
    editFormData,
    initializeEditForm,
    updatePdfName,
    validateAndSubmitEdit,
  };
};
